import { CSSProperties, useEffect, useState } from "react";
import axios, { AxiosError } from "axios";
import t from "../locales/translation";
import { ComboBoxProps, Tooltip } from "../models/PropsInterface";
import { AiFillExclamationCircle } from "react-icons/ai";
import Swal from "sweetalert2";
import ColorsUtils from "../utils/ColorsUtils";
import Modal from "../UI/Modal/Modal";
import TooltipModal from "../UI/TooltipModal/TooltipModal";
import "../cssStyles/ComboBox.css";
import { styleHelper } from "../../helpers/commonFunctions";

const ComboBox = (props: ComboBoxProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [load, setLoad] = useState<boolean>(false);

  /**************************************
   *   CUSTOME STYLES RETURNED FROM API
   * ************************************/

  const savingTagRgba = ColorsUtils.convertHexToRgb(
    props?.saving_tag && props?.saving_tag?.background_color
  );
  const bestOfferRgba = ColorsUtils.convertHexToRgb(
    props?.isBestOffer && props.best_offer.background_color
  );
  const backgroundButtonRgba = ColorsUtils.convertHexToRgb(
    props?.button?.background_color
  );

  const highlightedSubTitleStyle = {
    color: props.highlighted_subTitle_color,
  } as CSSProperties;

  const savingTagStyle = {
    color: props?.saving_tag?.text_color,
    backgroundColor: `${savingTagRgba
        ? `rgb(${savingTagRgba.r},${savingTagRgba.g},${savingTagRgba.b}, ${savingTagRgba.a})`
        : ""
      }`,
  } as CSSProperties;

  const buttonStyle = {
    fontWeight: props?.isBestOffer ? "bold" : "normal",
    fontSize: "16px",
    color: props?.button?.text_color,
    backgroundColor: `rgb(${backgroundButtonRgba.r},${backgroundButtonRgba.g},${backgroundButtonRgba.b}, ${backgroundButtonRgba.a})`,
    border: `1px solid ${props?.button?.border_color}`,
  };

  const bestOfferStyle = {
    backgroundColor: `${bestOfferRgba
        ? `rgb(${bestOfferRgba.r},${bestOfferRgba.g},${bestOfferRgba.b}, ${bestOfferRgba.a})`
        : ""
      }`,
  };

  /**************************************
   *         MOBILE CHECKER
   * ************************************/
  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const isMobileDevice = screenWidth <= 572; // Adjust the threshold value as needed

      setIsMobile(isMobileDevice);
    };

    // Attach the resize event listener
    window.addEventListener("resize", handleResize);

    // Check initial screen width on component mount
    handleResize();

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**************************************
   *            CHECKOUT
   * ************************************/
  const purchase = async () => {
    return axios.post(
      `${props.api}/checkout/create-cart`,
      {
        Payment: {
          productAssignId: props.product_id,
          modelId: props.modelId,
          absoluteUrl: props.referral_url ?? window.location.href,
          posSource: props.pos_source,
        },
      },
      {
        headers: {
          source: props.source,
          country: props.countryAbbreviation,
          [`enforce-language`]: props.language,
          [`Authorization`]: "Bearer " + props.token,
        },
      }
    );
  };

  const consumeBundle = async () => {
    return axios.post(
      `${props.api}/bundle-user/purchase`,
      {
        BundleUser: {
          id: props.product_id,
          postId: props.modelId,
        },
      },
      {
        headers: {
          source: props.source,
          country: props.countryAbbreviation,
          [`enforce-language`]: props.language,
          [`Authorization`]: "Bearer " + props.token,
        },
      }
    );
  };

  const handleCheckoutBasedOnButtonAction = (action: string) => {
    if (action !== "use") {
      purchase().then((response) => {
        if (response.status === 201) {
          setIsDisabled(true);
          return (window.top.location.href = response.data.redirect);
        }
      });
    } else {
      consumeBundle().then((response) => {
        if (response.status === 200) {
          setIsDisabled(true);
          props.setIsSuccessProcess(true);
          // parent.location.reload();
          // return window.top.location.href = `${url}/search/${props.modelId}`
        }
      });
    }
  };

  const checkout = () => {
    if (props.button?.confirmation) {
      return Swal.fire({
        icon: "warning",
        iconColor: "#0d6efd",
        html: `<div class="vas-pe-10 vas-ps-10 vas-fw-bolder">${props.button?.confirmation}</div>`,
        buttonsStyling: false,
        showCancelButton: true,
        cancelButtonText: t.rejectText,
        showLoaderOnConfirm: true,
        confirmButtonText: t.approveText,
        reverseButtons: props.language != "en",
        customClass: {
          confirmButton:
            "vas-btn vas-btn-lg vas-btn-primary vas-ps-3 vas-pe-3 vas-m-1 ",
          cancelButton:
            "vas-btn vas-btn-lg vas-btn-danger vas-ps-3 vas-pe-3 vas-m-1",
        },
        allowOutsideClick: () => !Swal.isLoading(),
      }).then((result) => {
        if (result.isConfirmed) {
          handleCheckoutBasedOnButtonAction(props.button.action);
        }
      });
    } else {
      handleCheckoutBasedOnButtonAction(props.button.action);
    }
  };

  const fetchToolTip = async (id: number, key: string) => {
    return axios.get(`${props.api}/vas/package-tooltip`, {
      params: {
        assignId: id,
        key: key,
      },
      headers: {
        source: props.source,
        country: props.countryAbbreviation,
        [`enforce-language`]: props.language,
        [`Authorization`]: "Bearer " + props.token,
      },
    });
  };

  /**************************************
   *            IMAGE FORMAT
   * ************************************/
  const getImgPath = (img: string): string => {
    const replaceArray = ["platform", "size"];
    const replaceWith = ["android", "xxh"];
    for (let i = 0; i < replaceArray.length; i++) {
      img = img?.replace(
        new RegExp("{" + replaceArray[i] + "}", "gi"),
        replaceWith[i]
      );
    }
    return img;
  };

  /**************************************
   *        OPEN TOOLTIP POPUP
   * ************************************/
  const tooltipHandler = (e, key: string, productAssignId: number) => {
    e.stopPropagation();
    setLoad(true);
    fetchToolTip(productAssignId, key)
      .then((response) => {
        if (response.data) {
          const toolTipConfig = response.data;
          setLoad(false);
          const htmlContent = toolTipConfig?.details
            ? toolTipConfig.details
              .map(
                (detail) => `
            <div class="combo-box_tooltip_container">
              <div class="combo-box_tooltip_icon_container vas-row ">
                <div class="combo-box_tooltip_icon_size vas-col-2"> <img class="vas combo-box_details_icon" alt="no src" src="${getImgPath(
                  detail.icon
                )}"/></div> <div class="vas-col-10 combo-box_title_tooltip_marging">${detail.title
                  }</div>
              </div>
              <div class="combo-box_tooltip_desc">${detail.description}</div>
            </div>
          `
              )
              .join("")
            : "";

          return Swal.fire({
            html: `<div class="combo-box_swal_text_container" dir="${props.language == "ar" ? "rtl" : "ltr"
              }">${htmlContent}</div>`,
            showCloseButton: true,
            title: `<div class="combo-box_swal_header"><div class="combo-box_tooltip_title_icon"><img  class="vas combo-box_tooltip_title_icon_size" src="${getImgPath(
              toolTipConfig.title.icon
            )}" alt="no image"/></div><div class="combo-box_tooltip_title_style">${toolTipConfig.title.text
              }</div></div>`,
            showCancelButton: false,
            showConfirmButton: false,
            customClass: {
              container: "combo-box_custom_swal_container", // Add a custom class for the container,
              popup: "combo-box_custom_swal_popup", // Add a custom class for the popup (modal)
              closeButton: "combo-box_custom_swal_close_button",
              htmlContainer: "combo-box_custom_swal_html_container",
              title: "combo-box_custom_swal_title",
            },
            allowOutsideClick: () => !Swal.isLoading(),
          });
        }
      })
      .catch((error: AxiosError) => {
        setLoad(false);
      });
  };

  return (
    <>
      <div
        className={`vas-row combo-box_parent_container ${!!props.isBestOffer && "combo-box_parent_container_gap_on_mobile"
          }`}
        dir={props.language == "ar" ? "rtl" : "ltr"}
        style={styleHelper({ position: "relative" })}
      >
        {!!props.isBestOffer && (
          <div
            style={bestOfferStyle}
            dir={props.language == "ar" ? "rtl" : "ltr"}
            className={"combo-box_best_selling"}
          >
            {" "}
            {t.topSelling}
          </div>
        )}
        <div
          onClick={checkout}
          style={styleHelper({
            cursor: isDisabled ? "default" : "pointer",
            pointerEvents: isDisabled ? "none" : "auto",
          })}
          className={`vas-row combo-box_container ${!!props.isBestOffer && "combo-box_container_best_selling"
            }`}
        >
          <div className={"combo-box_box_header"}>
            {props?.title_text && (
              <div className={"combo-box_box_title"}>{props.title_text}</div>
            )}
            {props.subTitle_text && (
              <div className={"combo-box_box_subtitle"}>
                {props.language != "ar" ? props.subTitle_text : ""}{" "}
                <strong className="vas" style={highlightedSubTitleStyle}>
                  {props.highlighted_subTitle_text}
                </strong>{" "}
                {props.language == "ar" ? props.subTitle_text : ""}
              </div>
            )}
          </div>
          <div className={"combo-box_seperator"}></div>
          {!isMobile && (
            <>
              {props?.price && (
                <div className={"combo-box_price_tags_container"}>
                  <div className={"combo-box_price_section"}>
                    {props?.price?.price && (
                      <div className={"combo-box_price_style"}>
                        {props.price.price}{" "}
                        <span className={"combo-box_price_placeholder"}>
                          {props.price.price_placeholder}
                        </span>
                      </div>
                    )}
                    {props?.price?.old_price && (
                      <div className={"combo-box_old_price_style"}>
                        {props.price.old_price}
                      </div>
                    )}
                  </div>
                  <div
                    className={`combo-box_tag_section ${!props.saving_tag && "combo-box_tag_height"
                      }`}
                  >
                    {props.saving_tag && (
                      <div
                        className={"combo-box_tag_container"}
                        style={savingTagStyle}
                      >
                        {props.saving_tag.text}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className={"combo-box_seperator"}></div>
            </>
          )}
          {props?.details_list?.length > 0 &&
            props.details_list.map((item) => {
              return (
                <div className={"combo-box_detail"}>
                  <div className={"combo-box_check_icon_text_container"}>
                    <div className={"combo-box_check_icon"}>
                      <img
                        src={getImgPath(item.icon)}
                        className={"vas combo-box_check_icon_size"}
                      />
                    </div>
                    <div>
                      {item.text}{" "}
                      <strong className="vas">{item.highlighted_text}</strong>
                    </div>
                  </div>
                  {item.tooltip_key && (
                    <div
                      className={"combo-box_exlamation_icon"}
                      onClick={(e) =>
                        tooltipHandler(e, item.tooltip_key, props.product_id)
                      }
                    >
                      <AiFillExclamationCircle
                        className={"combo-box_exlamation_icon_size"}
                        color={"#808083"}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          {isMobile && (
            <>
              <div className={"combo-box_seperator"}></div>
              {props?.price && (
                <div className={"combo-box_price_tags_container"}>
                  <div className={"combo-box_price_section"}>
                    <div>
                      {" "}
                      {props?.price?.old_price && (
                        <div className={"combo-box_old_price_style"}>
                          {props.price.old_price}
                        </div>
                      )}
                      {props?.price?.price && (
                        <div className={"combo-box_price_style"}>
                          {props.price.price}{" "}
                          <span className={"combo-box_price_placeholder"}>
                            {props.price.price_placeholder}
                          </span>
                        </div>
                      )}
                      <div className={"combo-box_tag_section"}>
                        {props.saving_tag && (
                          <div
                            className={"combo-box_tag_container"}
                            style={savingTagStyle}
                          >
                            {props.saving_tag.text}
                          </div>
                        )}{" "}
                      </div>
                    </div>
                    {props.button && isMobile && (
                      <div className={"combo-box_button_container"}>
                        <button
                          style={buttonStyle}
                          className={"vas combo-box_button"}
                        >
                          {props.button.text}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          {props.button && !isMobile && (
            <div className={"combo-box_button_container"}>
              <button style={buttonStyle} className={"vas combo-box_button"}>
                {props.button.text}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ComboBox;
